import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ORDERS_SU2M } from "@/client-admin/types.gen.ts";
import { DataTableColumnHeader } from "../UserTable/data-table-column-header";

export const columns: ColumnDef<ORDERS_SU2M>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "intradayCode",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Intraday Code" />,
    cell: ({ row }) => <div>{row.getValue("intradayCode") ?? "-"}</div>,
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "date",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    enableColumnFilter: true,
    cell: ({ row }) => <div>{row.getValue("date") ?? "-"}</div>,
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "hour",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Hour" />,
    cell: ({ row }) => <div>{row.getValue("hour") ?? "-"}</div>,
  },
  {
    accessorKey: "quarter",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Quarter" />,
    cell: ({ row }) => <div>{row.getValue("quarter") ?? "-"}</div>,
  },
  {
    accessorKey: "orderType",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Order Type" />,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const value = row.getValue("orderType") as string | null | undefined;
      if (!value) return <div>-</div>;

      const className = getOrderTypeColor(value);
      return (
        <Badge className={className} variant="outline">
          {value}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "volume",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Volume" />,
    cell: ({ row }) => <div>{Number(row.getValue("volume")).toFixed(4) ?? "-"}</div>,
  },
  {
    accessorKey: "limitPrice",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Limit Price" />,
    cell: ({ row }) => <div>{Number(row.getValue("limitPrice")).toFixed(4) ?? "-"}</div>,
  },
  {
    accessorKey: "orderEntryTime",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Order Entry Time" />,
    cell: ({ row }) => (
      <div>{format(new Date(row.getValue("orderEntryTime") as string), "yyyy-MM-dd HH:mm:ss") ?? "-"}</div>
    ),
  },
  {
    accessorKey: "statusBCI",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status BCI" />,
    cell: ({ row }) => {
      const value = row.getValue("statusBCI") as string | null | undefined;
      if (!value) return <div>-</div>;

      const className = getStatusColor(value);
      return (
        <Badge className={className} variant="outline">
          {value}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },

  //   {
  //     accessorKey: "orderEntryTimeETPA",
  //     header: ({ column }) => <DataTableColumnHeader column={column} title="Created at" />,
  //     cell: ({ row }) => <div>{formatAmsterdamDateTime(row.getValue("created_at") as string)}</div>,
  //   },
];

export const statusesBCI = [
  {
    label: "TRADED",
    value: "TRADED",
  },
  {
    label: "UPDATED",
    value: "UPDATED",
  },
  {
    label: "PARTLY TRADED",
    value: "PARTLY TRADED",
  },
  {
    label: "PROCESSED",
    value: "PROCESSED",
  },
  {
    label: "REJECTED",
    value: "REJECTED",
  },
  {
    label: "DELETED",
    value: "DELETED",
  },
  {
    label: "Empty",
    value: null,
  },
];

export const orderTypes = [
  {
    label: "BUY",
    value: "BUY",
  },
  {
    label: "SELL",
    value: "SELL",
  },
];

function getOrderTypeColor(orderType: string | null | undefined): string {
  if (!orderType) return "bg-gray-100 text-gray-800";
  return orderType === "BUY" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
}

function getStatusColor(status: string | null | undefined): string {
  if (!status) return "bg-gray-100 text-gray-800";

  switch (status.toUpperCase()) {
    case "TRADED":
      return "bg-blue-100 text-blue-800";
    case "UPDATED":
    case "PROCESSED":
      return "bg-green-100 text-green-800";
    case "PARTLY TRADED":
      return "bg-yellow-100 text-yellow-800";
    case "REJECTED":
      return "bg-red-100 text-red-800";
    case "DELETED":
      return "bg-gray-300 text-gray-700";
    default:
      return "bg-gray-100 text-gray-800";
  }
}
export function getUniqueIntradayCodesFromSumOrders(data: ORDERS_SU2M[]): { label: string; value: string }[] {
  const uniqueCodes = [
    ...new Set(data.map((order) => order.intradayCode).filter((code): code is string => code !== null)),
  ];
  return uniqueCodes.sort().map((code) => ({ label: code, value: code }));
}

export function getUniqueDatesFromSumOrders(data: ORDERS_SU2M[]): { label: string; value: string }[] {
  const uniqueDates = [
    ...new Set(data.map((item) => item.date).filter((date): date is string => date !== null && date !== undefined)),
  ];
  return uniqueDates
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map((date) => ({
      label: date,
      value: date,
    }));
}
